// src\pages\admin\BlogManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBlogAdmin } from '../../hooks/useBlogAdmin';
import { useDebounce } from '../../hooks/useDebounce';
import { blogService } from '../../services/blogService';
import { useToast } from '../../hooks/useToast';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiTrash2,
  FiEdit2,
  FiCopy,
  FiEye,
  FiXCircle,
  FiCalendar,
  FiStar,
  FiStar as FiStarOutline,
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiList,
  FiRefreshCw,
  FiImage,
  FiClock,
  FiTrendingUp,
  FiTag,
  FiFolder,
  FiSave,
  FiDownload,
  FiUploadCloud,
  FiAlertCircle,
  FiCheckCircle,
  FiMessageCircle,
  FiBell,
  FiX,
  FiBarChart2,
  FiUsers,
  FiTrendingDown,
} from 'react-icons/fi';
import CommentManager from '../../components/admin/blog/CommentManager';
import BlogAnalytics from '../../components/admin/blog/BlogAnalytics';
import BlogEditorModal from '../../components/admin/blog/BlogEditorModal';

const BlogManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const { showToast } = useToast();
  const { isAdmin, getAuthToken } = useAuth();
  const {
    blogs,
    loading,
    stats,
    pagination,
    fetchBlogs,
    createBlog,
    updateBlog,
    deleteBlog,
    bulkDelete,
    bulkUpdateStatus,
    toggleFeatured,
  } = useBlogAdmin();

  // State
  const [viewMode, setViewMode] = useState('table');
  const [selectedBlogs, setSelectedBlogs] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    tag: '',
    featured: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const debouncedSearch = useDebounce(searchTerm, 300);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [editorPendingImages, setEditorPendingImages] = useState([]);
  const [imagesReadyForSubmit, setImagesReadyForSubmit] = useState(false);

  const [commentCounts, setCommentCounts] = useState({});
  const [showCommentManager, setShowCommentManager] = useState(false);
  const [selectedBlogForComments, setSelectedBlogForComments] = useState(null);

  // FILTER SYSTEM
  const [filterBlogId, setFilterBlogId] = useState(null);
  const [activeNotificationId, setActiveNotificationId] = useState(null);
  const [showFilterBanner, setShowFilterBanner] = useState(false);
  const [filteredBlogTitle, setFilteredBlogTitle] = useState('');
  const [filteredBlogImage, setFilteredBlogImage] = useState(null);

  const [unreadFilter, setUnreadFilter] = useState(false);
  const [unreadCommentCounts, setUnreadCommentCounts] = useState({});
  const [loadingUnreadCounts, setLoadingUnreadCounts] = useState(false);

  // Auto-save states
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [editTab, setEditTab] = useState('editor');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    status: 'draft',
    featured: false,
    scheduledAt: null,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [],
    canonicalUrl: '',
    ogImage: '',
    ogTitle: '',
    ogDescription: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    imageAlt: '',
    imageCaption: '',
    slug: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reading Analytics State
  const [readingStats, setReadingStats] = useState({
    totalReadingTime: 0,
    averageReadingTime: 0,
    totalReads: 0,
    mostReadBlog: null,
    loading: false,
  });

  // Pagination
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // ============================================
  // READING ANALYTICS FUNCTIONS
  // ============================================

  /**
   * Format time for display
   */
  const formatTime = minutes => {
    if (!minutes || minutes === 0) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  /**
   * Calculate reading statistics from blogs
   * Uses separate fields:
   * - readingTime: Estimated (from word count) - Public
   * - avgReadingTime: Tracked average - Admin
   * - totalReadingTime: Tracked total - Admin
   * - readingCount: Tracked count - Admin
   */
  const calculateReadingStats = useCallback(() => {
    if (!blogs || blogs.length === 0) {
      setReadingStats({
        totalReadingTime: 0,
        averageReadingTime: 0,
        totalReads: 0,
        mostReadBlog: null,
        loading: false,
      });
      return;
    }

    setReadingStats(prev => ({ ...prev, loading: true }));

    try {
      const publishedBlogs = blogs.filter(b => b.status === 'published');

      if (publishedBlogs.length === 0) {
        setReadingStats({
          totalReadingTime: 0,
          averageReadingTime: 0,
          totalReads: 0,
          mostReadBlog: null,
          loading: false,
        });
        return;
      }

      // Calculate TOTAL reading time from tracked data (in minutes)
      const totalReadingTime = publishedBlogs.reduce(
        (sum, blog) => sum + (blog.totalReadingTime || 0),
        0,
      );

      // Convert seconds to minutes for display
      const totalReadingTimeMinutes = Math.round(totalReadingTime / 60);

      // Calculate AVERAGE reading time from tracked data (in minutes)
      const totalAvgReadingTime = publishedBlogs.reduce(
        (sum, blog) => sum + (blog.avgReadingTime || 0),
        0,
      );
      const averageReadingTime =
        publishedBlogs.length > 0
          ? Math.round(totalAvgReadingTime / publishedBlogs.length)
          : 0;

      // Calculate total reads
      const totalReads = publishedBlogs.reduce(
        (sum, blog) => sum + (blog.readingCount || 0),
        0,
      );

      // Find most read blog
      const mostReadBlog =
        [...publishedBlogs].sort(
          (a, b) => (b.readingCount || 0) - (a.readingCount || 0),
        )[0] || null;

      setReadingStats({
        totalReadingTime: totalReadingTimeMinutes,
        averageReadingTime,
        totalReads,
        mostReadBlog,
        loading: false,
      });
    } catch (error) {
      console.error('Error calculating reading stats:', error);
      setReadingStats(prev => ({ ...prev, loading: false }));
    }
  }, [blogs]);

  // Recalculate reading stats when blogs change
  useEffect(() => {
    calculateReadingStats();
  }, [calculateReadingStats]);

  // ============================================
  // NOTIFICATION COUNT FUNCTIONS
  // ============================================

  const fetchCommentCounts = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const notificationsRef = collection(db, 'notifications');
      const blogCommentsQuery = query(
        notificationsRef,
        where('type', 'in', ['blog-comment', 'blog-reply', 'blog-reaction']),
        where('read', '==', false),
      );

      const snapshot = await getDocs(blogCommentsQuery);
      const counts = {};

      snapshot.forEach(doc => {
        const data = doc.data();
        const blogId = data.data?.blogId;
        if (blogId) {
          counts[blogId] = (counts[blogId] || 0) + 1;
        }
      });

      setCommentCounts(counts);
    } catch (error) {
      console.error('Failed to fetch comment counts:', error);
    }
  }, [getAuthToken]);

  const fetchUnreadCommentCounts = useCallback(async () => {
    setLoadingUnreadCounts(true);
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(
        '/api/blogs?action=get-unread-comment-counts',
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      if (data.success) {
        setUnreadCommentCounts(data.counts || {});
      }
    } catch (error) {
      console.error('Failed to fetch unread counts:', error);
    } finally {
      setLoadingUnreadCounts(false);
    }
  }, [getAuthToken]);

  // Real-time listeners for notifications
  useEffect(() => {
    if (!isAdmin) return;

    fetchCommentCounts();
    fetchUnreadCommentCounts();

    const notificationsRef = collection(db, 'notifications');
    const blogCommentsQuery = query(
      notificationsRef,
      where('type', 'in', ['blog-comment', 'blog-reply', 'blog-reaction']),
      where('read', '==', false),
    );

    const unsubscribe = onSnapshot(blogCommentsQuery, () => {
      fetchCommentCounts();
      fetchUnreadCommentCounts();
    });

    const interval = setInterval(fetchUnreadCommentCounts, 30000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [isAdmin, fetchCommentCounts, fetchUnreadCommentCounts]);

  // ============================================
  // URL PARAMETER HANDLING - AUTO FILTER
  // ============================================

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const blogId = params.get('filter');
    const notificationId = params.get('notification');

    if (blogId && notificationId) {
      setFilterBlogId(blogId);
      setActiveNotificationId(notificationId);
      setShowFilterBanner(true);

      const markAsRead = async () => {
        try {
          const token = await getAuthToken();
          if (!token) return;
          await fetch('/api/blogs?action=mark-notification-read', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ notificationId }),
          });
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
        }
      };
      markAsRead();

      const fetchBlogDetails = async () => {
        try {
          const token = await getAuthToken();
          if (!token) return;
          const response = await fetch(
            `/api/blogs?action=single-admin&id=${blogId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          const data = await response.json();
          if (data.success && data.blog) {
            setFilteredBlogTitle(data.blog.title);
            setFilteredBlogImage(data.blog.featuredImage || null);
          }
        } catch (error) {
          console.error('Failed to fetch blog details:', error);
        }
      };
      fetchBlogDetails();

      setTimeout(() => {
        setFilterBlogId(null);
        setActiveNotificationId(null);
        setShowFilterBanner(false);
        setFilteredBlogTitle('');
        setFilteredBlogImage(null);
        navigate('/admin/blog', { replace: true });
      }, 15000);
    }
  }, [location, navigate, getAuthToken]);

  // ============================================
  // FILTERED BLOGS
  // ============================================

  const filteredBlogs = useMemo(() => {
    let result = [...blogs];

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        blog =>
          blog.title.toLowerCase().includes(searchLower) ||
          blog.excerpt?.toLowerCase().includes(searchLower) ||
          blog.tags?.some(t => t.toLowerCase().includes(searchLower)),
      );
    }

    if (filters.status) {
      result = result.filter(blog => blog.status === filters.status);
    }

    if (filters.category) {
      result = result.filter(blog => blog.category === filters.category);
    }

    if (filters.tag) {
      result = result.filter(blog => blog.tags?.includes(filters.tag));
    }

    if (filters.featured) {
      result = result.filter(
        blog => blog.featured === (filters.featured === 'true'),
      );
    }

    if (unreadFilter) {
      result = result.filter(blog => unreadCommentCounts[blog.id] > 0);
    }

    if (filterBlogId) {
      result = result.filter(blog => blog.id === filterBlogId);
    }

    result.sort((a, b) => {
      let aVal = a[filters.sortBy];
      let bVal = b[filters.sortBy];
      if (
        filters.sortBy === 'createdAt' ||
        filters.sortBy === 'updatedAt' ||
        filters.sortBy === 'publishedAt'
      ) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      if (filters.sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return result;
  }, [
    blogs,
    debouncedSearch,
    filters,
    unreadFilter,
    unreadCommentCounts,
    filterBlogId,
  ]);

  /**
   * Get reading time badge color
   */
  const getReadingTimeColor = minutes => {
    if (!minutes || minutes <= 0)
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    if (minutes <= 3) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    } else if (minutes <= 7) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    } else if (minutes <= 12) {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    } else {
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    }
  };

  // ============================================
  // COMMENT MANAGER HANDLER
  // ============================================

  const handleOpenCommentManager = async blog => {
    setSelectedBlogForComments(blog);
    setShowCommentManager(true);

    setCommentCounts(prev => ({ ...prev, [blog.id]: 0 }));
    setUnreadCommentCounts(prev => ({ ...prev, [blog.id]: 0 }));

    try {
      const token = await getAuthToken();
      if (!token) return;

      await fetch('/api/blogs?action=mark-comment-notifications-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ blogId: blog.id }),
      });
    } catch (error) {
      console.error('Failed to reset comment notifications:', error);
    }
  };

  const clearNotificationFilter = () => {
    setFilterBlogId(null);
    setActiveNotificationId(null);
    setShowFilterBanner(false);
    setFilteredBlogTitle('');
    setFilteredBlogImage(null);
    navigate('/admin/blog', { replace: true });
  };

  // ============================================
  // BLOG DATA LOADING
  // ============================================

  const loadBlogs = useCallback(
    async (page = pagination.currentPage) => {
      await fetchBlogs({
        page,
        limit: 50,
        status: filters.status || undefined,
        category: filters.category || undefined,
        tag: filters.tag || undefined,
        featured: filters.featured || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        search: debouncedSearch || undefined,
      });
    },
    [fetchBlogs, filters, debouncedSearch, pagination.currentPage],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      loadBlogs(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [
    filters.status,
    filters.category,
    filters.tag,
    filters.featured,
    filters.sortBy,
    filters.sortOrder,
    debouncedSearch,
    loadBlogs,
  ]);

  // ============================================
  // CATEGORIES & TAGS LOADING
  // ============================================

  const loadCategoriesAndTags = useCallback(async () => {
    try {
      const storedCategories = localStorage.getItem('blog_categories');
      if (storedCategories) setCategories(JSON.parse(storedCategories));

      const [categoriesRes, tagsRes] = await Promise.all([
        blogService.getCategories(),
        blogService.getTags(),
      ]);

      const apiCategories = categoriesRes.categories || [];
      const stored = JSON.parse(
        localStorage.getItem('blog_categories') || '[]',
      );
      const allCategories = [
        ...new Map(
          [...stored, ...apiCategories].map(c => [c.name, c]),
        ).values(),
      ];
      setCategories(allCategories);
      setTags(tagsRes.tags || []);
    } catch (error) {
      console.error('Failed to load categories/tags:', error);
    }
  }, []);

  useEffect(() => {
    loadCategoriesAndTags();
  }, [loadCategoriesAndTags]);

  // ============================================
  // FORM RESET
  // ============================================

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      tags: [],
      status: 'draft',
      featured: false,
      scheduledAt: null,
      seoTitle: '',
      seoDescription: '',
      seoKeywords: [],
      canonicalUrl: '',
      ogImage: '',
      ogTitle: '',
      ogDescription: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
      imageAlt: '',
      imageCaption: '',
      slug: '',
    });
    setImageFile(null);
    setImagePreview('');
    setCurrentBlog(null);
    setEditorPendingImages([]);
    setImagesReadyForSubmit(false);
  };

  // ============================================
  // FILTER HANDLERS
  // ============================================

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      category: '',
      tag: '',
      featured: '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
    setSearchTerm('');
    setUnreadFilter(false);
  };

  // ============================================
  // EXPORT FUNCTION
  // ============================================

  const handleExportBlogs = () => {
    const exportData = {
      blogs: filteredBlogs,
      exportedAt: new Date().toISOString(),
      totalCount: filteredBlogs.length,
      readingStats: {
        totalReadingTime: readingStats.totalReadingTime,
        averageReadingTime: readingStats.averageReadingTime,
        totalReads: readingStats.totalReads,
      },
    };
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri =
      'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `blogs_export_${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    showToast('Blogs exported successfully', 'success');
  };

  // ============================================
  // QUICK STATS
  // ============================================

  const quickStats = useMemo(() => {
    if (!filteredBlogs.length) return null;
    const totalViews = filteredBlogs.reduce(
      (sum, blog) => sum + (blog.views || 0),
      0,
    );
    const avgViews = Math.round(totalViews / filteredBlogs.length);
    const mostViewed = [...filteredBlogs].sort(
      (a, b) => (b.views || 0) - (a.views || 0),
    )[0];
    return { totalViews, avgViews, mostViewed };
  }, [filteredBlogs]);

  // ============================================
  // CRUD HANDLERS
  // ============================================

  const handleCreateBlog = async submitData => {
    setIsSubmitting(true);
    try {
      console.log('📤 handleCreateBlog called');
      // The submitData is already FormData from the modal
      const result = await createBlog(submitData);
      console.log('✅ Blog created:', result);

      // Reset state on success
      resetForm();
      setShowCreateModal(false);

      // Refresh the blog list
      await loadBlogs(1);

      return result;
    } catch (error) {
      console.error('❌ Create failed:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateBlog = async submitData => {
    if (!currentBlog) return;

    setIsSubmitting(true);
    try {
      console.log('📤 handleUpdateBlog called for:', currentBlog.id);
      // The submitData is already FormData from the modal
      const result = await updateBlog(currentBlog.id, submitData);
      console.log('✅ Blog updated:', result);

      // Reset state on success
      resetForm();
      setShowEditModal(false);
      setCurrentBlog(null);

      // Refresh the blog list
      await loadBlogs(pagination.currentPage);

      return result;
    } catch (error) {
      console.error('❌ Update failed:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBlog = async () => {
    if (!currentBlog) return;
    try {
      await deleteBlog(currentBlog.id);
      showToast('Blog deleted successfully', 'success');
      setShowDeleteModal(false);
      setCurrentBlog(null);
      await loadBlogs(pagination.currentPage);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBlogs.length === 0) return;
    if (
      window.confirm(
        `Delete ${selectedBlogs.length} blog(s)? This action cannot be undone.`,
      )
    ) {
      try {
        await bulkDelete(selectedBlogs);
        showToast(
          `${selectedBlogs.length} blog(s) deleted successfully`,
          'success',
        );
        setSelectedBlogs([]);
        await loadBlogs(1);
      } catch (error) {
        showToast(error.message, 'error');
      }
    }
  };

  const handleBulkStatusUpdate = async status => {
    if (selectedBlogs.length === 0) return;
    try {
      await bulkUpdateStatus(selectedBlogs, status);
      showToast(
        `${selectedBlogs.length} blog(s) updated to ${status}`,
        'success',
      );
      setSelectedBlogs([]);
      await loadBlogs(1);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleToggleFeatured = async (id, currentFeatured) => {
    try {
      await toggleFeatured(id, !currentFeatured);
      showToast(
        `Blog ${!currentFeatured ? 'featured' : 'unfeatured'} successfully`,
        'success',
      );
      await loadBlogs(pagination.currentPage);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleEditBlog = async blog => {
    setCurrentBlog(blog);
    setShowEditModal(true);
  };

  const handleDuplicateBlog = async blog => {
    try {
      const duplicatedData = {
        ...blog,
        title: `${blog.title} (Copy)`,
        status: 'draft',
        featured: false,
        publishedAt: null,
        scheduledAt: null,
        views: 0,
        likes: 0,
      };
      delete duplicatedData.id;

      const formDataObj = new FormData();
      Object.entries(duplicatedData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            formDataObj.append(key, JSON.stringify(value));
          } else if (key !== 'content') {
            formDataObj.append(key, value);
          }
        }
      });
      formDataObj.append('content', blog.content);

      await createBlog(formDataObj);
      showToast('Blog duplicated successfully', 'success');
      await loadBlogs(1);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handlePreviewBlog = blog => {
    window.open(`/blog/${blog.slug}`, '_blank');
  };

  const toggleSelection = id => {
    setSelectedBlogs(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
    );
  };

  const toggleAllSelection = () => {
    if (selectedBlogs.length === filteredBlogs.length) {
      setSelectedBlogs([]);
    } else {
      setSelectedBlogs(filteredBlogs.map(b => b.id));
    }
  };

  const getStatusBadge = status => {
    const styles = {
      published:
        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      draft:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      scheduled:
        'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      archived: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
    };
    return styles[status] || styles.draft;
  };

  const totalUnreadCount = Object.values(unreadCommentCounts).reduce(
    (a, b) => a + b,
    0,
  );

  // Pagination
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterBlogId, debouncedSearch, filters, unreadFilter]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg)' }}>
      {/* Filter Banner */}
      {showFilterBanner && filteredBlogTitle && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 px-4 py-3 rounded-lg bg-blue-500 text-white shadow-lg animate-fade-in-up flex items-center gap-3 max-w-md">
          {filteredBlogImage && (
            <img
              src={filteredBlogImage}
              alt={filteredBlogTitle}
              className="w-8 h-8 rounded object-cover"
            />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <FiBell className="w-4 h-4 animate-pulse" />
              <span className="text-sm font-medium">New Activity</span>
            </div>
            <p className="text-xs opacity-90">Showing: {filteredBlogTitle}</p>
          </div>
          <button
            onClick={clearNotificationFilter}
            className="p-1 rounded-full hover:bg-white/20 transition-all"
            title="Show all blogs"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text)' }}>
              Blog Management
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
              Manage, create, and organize your blog posts
              {filterBlogId && filteredBlogTitle && (
                <span className="ml-2 text-blue-500">
                  (Filtered: {filteredBlogTitle})
                </span>
              )}
              {unreadFilter && (
                <span className="ml-2 text-orange-500">
                  (Showing unread comments only)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                showAnalytics ? 'bg-black/10 dark:bg-white/10' : ''
              }`}
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            >
              <FiBarChart2 className="w-4 h-4" />
              <span className="hidden sm:inline">
                {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
              </span>
            </button>
            <button
              onClick={handleExportBlogs}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              title="Export blogs as JSON"
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              onClick={() => loadBlogs(1)}
              className="p-2 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
              title="Refresh"
            >
              <FiRefreshCw
                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
              />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
            >
              <FiPlus className="w-4 h-4" />
              New Post
            </button>
          </div>
        </div>

        {/* Reading Analytics Stats Cards */}
        {!readingStats.loading && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  Total Reading Time
                </p>
                <FiClock className="w-4 h-4 text-blue-500" />
              </div>
              <p
                className="text-2xl font-bold mt-1"
                style={{ color: 'var(--text)' }}
              >
                {formatTime(readingStats.totalReadingTime)}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                Tracked from user sessions
              </p>
            </div>

            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  Avg. Reading Time
                </p>
                <FiTrendingUp className="w-4 h-4 text-green-500" />
              </div>
              <p
                className="text-2xl font-bold mt-1"
                style={{ color: 'var(--text)' }}
              >
                {readingStats.averageReadingTime || 0} min
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                Average actual reading time
              </p>
            </div>

            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  Total Reads
                </p>
                <FiUsers className="w-4 h-4 text-purple-500" />
              </div>
              <p
                className="text-2xl font-bold mt-1"
                style={{ color: 'var(--text)' }}
              >
                {readingStats.totalReads.toLocaleString()}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                Reading sessions
              </p>
            </div>

            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  Most Read
                </p>
                <FiTrendingUp className="w-4 h-4 text-orange-500" />
              </div>
              {readingStats.mostReadBlog ? (
                <>
                  <p
                    className="text-sm font-semibold mt-1 truncate"
                    style={{ color: 'var(--text)' }}
                  >
                    {readingStats.mostReadBlog.title}
                  </p>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--muted)' }}
                  >
                    {readingStats.mostReadBlog.readingCount || 0} reads
                  </p>
                </>
              ) : (
                <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                  No data yet
                </p>
              )}
            </div>
          </div>
        )}

        {/* Quick Stats Cards */}
        {stats && quickStats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Total Posts
              </p>
              <p
                className="text-2xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                {stats.total}
              </p>
            </div>
            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Published
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.published}
              </p>
            </div>
            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Drafts
              </p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.draft}
              </p>
            </div>
            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Scheduled
              </p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.scheduled}
              </p>
            </div>
            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Total Views
              </p>
              <p
                className="text-2xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                {quickStats.totalViews.toLocaleString()}
              </p>
            </div>
            <div
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Avg Views/Post
              </p>
              <p
                className="text-2xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                {quickStats.avgViews.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {showAnalytics && (
          <div className="mt-8">
            <BlogAnalytics />
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <FiSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: 'var(--muted)' }}
              />
              <input
                type="text"
                placeholder="Search blogs by title, excerpt, or tags..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setUnreadFilter(false)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all duration-200 ${
                  !unreadFilter
                    ? 'bg-blue-500 text-white'
                    : 'border hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ borderColor: 'var(--border)' }}
              >
                All Posts
              </button>
              <button
                onClick={() => setUnreadFilter(true)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all duration-200 flex items-center gap-2 ${
                  unreadFilter
                    ? 'bg-blue-500 text-white'
                    : 'border hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ borderColor: 'var(--border)' }}
              >
                <FiMessageCircle className="w-3 h-3" />
                Unread Comments
                {totalUnreadCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white">
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </span>
                )}
              </button>
              {loadingUnreadCounts && (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${showFilters ? 'bg-black/5 dark:bg-white/5' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
              >
                <FiFilter className="w-4 h-4" />
                Filters
                {(filters.status ||
                  filters.category ||
                  filters.tag ||
                  filters.featured) && (
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                )}
              </button>
              <div
                className="flex items-center gap-1 border rounded-lg p-1"
                style={{ borderColor: 'var(--border)' }}
              >
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded transition-all ${viewMode === 'table' ? 'bg-black/10 dark:bg-white/10' : ''}`}
                  title="Table View"
                >
                  <FiList className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-all ${viewMode === 'grid' ? 'bg-black/10 dark:bg-white/10' : ''}`}
                  title="Grid View"
                >
                  <FiGrid className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div
              className="mt-4 p-4 rounded-xl border animate-fade-in-up"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <select
                  value={filters.status}
                  onChange={e => handleFilterChange('status', e.target.value)}
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <option value="">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="archived">Archived</option>
                </select>
                <select
                  value={filters.category}
                  onChange={e => handleFilterChange('category', e.target.value)}
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
                <select
                  value={filters.tag}
                  onChange={e => handleFilterChange('tag', e.target.value)}
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <option value="">All Tags</option>
                  {tags.map(tag => (
                    <option key={tag.name} value={tag.name}>
                      {tag.name} ({tag.count})
                    </option>
                  ))}
                </select>
                <select
                  value={filters.featured}
                  onChange={e => handleFilterChange('featured', e.target.value)}
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <option value="">All Posts</option>
                  <option value="true">Featured Only</option>
                  <option value="false">Non-Featured Only</option>
                </select>
                <select
                  value={filters.sortBy}
                  onChange={e => handleFilterChange('sortBy', e.target.value)}
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <option value="createdAt">Sort by Created Date</option>
                  <option value="updatedAt">Sort by Updated Date</option>
                  <option value="publishedAt">Sort by Published Date</option>
                  <option value="title">Sort by Title</option>
                  <option value="views">Sort by Views</option>
                  <option value="readingCount">Sort by Reads</option>
                  <option value="avgReadingTime">Sort by Avg Reading</option>
                </select>
                <select
                  value={filters.sortOrder}
                  onChange={e =>
                    handleFilterChange('sortOrder', e.target.value)
                  }
                  className="px-3 py-2 rounded-lg border bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <option value="desc">Descending (Newest First)</option>
                  <option value="asc">Ascending (Oldest First)</option>
                </select>
              </div>
              <div className="flex justify-between mt-4">
                <div className="text-xs" style={{ color: 'var(--muted)' }}>
                  {filteredBlogs.length} results found
                </div>
                <button
                  onClick={clearFilters}
                  className="text-sm hover:underline"
                  style={{ color: 'var(--muted)' }}
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedBlogs.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex flex-wrap items-center justify-between gap-3 animate-fade-in-up">
            <div className="flex items-center gap-2">
              <FiCheckCircle className="w-4 h-4 text-blue-500" />
              <span className="text-sm" style={{ color: 'var(--text)' }}>
                {selectedBlogs.length} blog(s) selected
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleBulkStatusUpdate('published')}
                className="px-3 py-1 rounded text-sm bg-green-500 text-white hover:bg-green-600 transition-all"
              >
                <FiUploadCloud className="w-3 h-3 inline mr-1" /> Publish
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('draft')}
                className="px-3 py-1 rounded text-sm bg-yellow-500 text-white hover:bg-yellow-600 transition-all"
              >
                <FiSave className="w-3 h-3 inline mr-1" /> Move to Draft
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 rounded text-sm bg-red-500 text-white hover:bg-red-600 transition-all"
              >
                <FiTrash2 className="w-3 h-3 inline mr-1" /> Delete
              </button>
            </div>
          </div>
        )}

        {/* Blog List - Table View */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="animate-spin rounded-full h-12 w-12 border-b-2"
              style={{ borderColor: 'var(--text)' }}
            />
          </div>
        ) : viewMode === 'table' ? (
          <div
            className="overflow-x-auto rounded-xl border"
            style={{ borderColor: 'var(--border)' }}
          >
            <table className="min-w-full">
              <thead>
                <tr
                  className="border-b"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={
                        selectedBlogs.length === paginatedBlogs.length &&
                        paginatedBlogs.length > 0
                      }
                      onChange={toggleAllSelection}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Image
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                    onClick={() => handleFilterChange('sortBy', 'title')}
                  >
                    Title{' '}
                    {filters.sortBy === 'title' &&
                      (filters.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Status
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                    onClick={() => handleFilterChange('sortBy', 'views')}
                  >
                    Views{' '}
                    {filters.sortBy === 'views' &&
                      (filters.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                    onClick={() => handleFilterChange('sortBy', 'readingCount')}
                  >
                    Reads{' '}
                    {filters.sortBy === 'readingCount' &&
                      (filters.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                    onClick={() =>
                      handleFilterChange('sortBy', 'avgReadingTime')
                    }
                  >
                    Avg Reading{' '}
                    {filters.sortBy === 'avgReadingTime' &&
                      (filters.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                    onClick={() => handleFilterChange('sortBy', 'createdAt')}
                  >
                    Date{' '}
                    {filters.sortBy === 'createdAt' &&
                      (filters.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedBlogs.map(blog => (
                  <tr
                    key={blog.id}
                    id={`blog-${blog.id}`}
                    className={`border-b hover:bg-black/5 dark:hover:bg-white/5 transition-all duration-300 ${filterBlogId === blog.id ? 'animate-pulse bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedBlogs.includes(blog.id)}
                        onChange={() => toggleSelection(blog.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {blog.featuredImage ? (
                        <img
                          src={blog.featuredImage}
                          alt={blog.title}
                          className="w-12 h-12 rounded object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <FiImage className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p
                          className="font-medium line-clamp-1"
                          style={{ color: 'var(--text)' }}
                        >
                          {blog.title}
                        </p>
                        <p
                          className="text-xs mt-1"
                          style={{ color: 'var(--muted)' }}
                        >
                          {blog.slug}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                        {blog.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(blog.status)}`}
                      >
                        {blog.status}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      {blog.views || 0}
                    </td>
                    <td
                      className="px-4 py-3 text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      {blog.readingCount || 0}
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getReadingTimeColor(blog.avgReadingTime || 1)}`}
                      >
                        <FiClock className="w-3 h-3" />
                        <span>{blog.avgReadingTime || 1} min</span>
                      </div>
                    </td>
                    <td
                      className="px-4 py-3 text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      {blog.publishedAt
                        ? new Date(blog.publishedAt).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <button
                          onClick={() => handleEditBlog(blog)}
                          className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                          title="Edit"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicateBlog(blog)}
                          className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                          title="Duplicate"
                        >
                          <FiCopy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePreviewBlog(blog)}
                          className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                          title="Preview"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenCommentManager(blog)}
                          className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5 relative"
                          title="Manage Comments"
                        >
                          <FiMessageCircle className="w-4 h-4" />
                          {commentCounts[blog.id] > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                              {commentCounts[blog.id] > 99
                                ? '99+'
                                : commentCounts[blog.id]}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() =>
                            handleToggleFeatured(blog.id, blog.featured)
                          }
                          className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                          title={blog.featured ? 'Unfeature' : 'Feature'}
                        >
                          {blog.featured ? (
                            <FiStar className="w-4 h-4 text-yellow-500" />
                          ) : (
                            <FiStarOutline className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setCurrentBlog(blog);
                            setShowDeleteModal(true);
                          }}
                          className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedBlogs.map(blog => (
              <div
                key={blog.id}
                className={`rounded-xl border overflow-hidden hover:shadow-lg transition-all duration-300 ${filterBlogId === blog.id ? 'animate-pulse ring-2 ring-blue-500' : ''}`}
                style={{ borderColor: 'var(--border)' }}
              >
                {blog.featuredImage ? (
                  <img
                    src={blog.featuredImage}
                    alt={blog.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <FiImage className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(blog.status)}`}
                    >
                      {blog.status}
                    </span>
                    <div className="flex items-center gap-2">
                      {blog.featured && (
                        <FiStar className="w-4 h-4 text-yellow-500" />
                      )}
                      <div
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${getReadingTimeColor(blog.avgReadingTime || 1)}`}
                      >
                        <FiClock className="w-3 h-3" />
                        <span>{blog.avgReadingTime || 1} min</span>
                      </div>
                    </div>
                  </div>
                  <h3
                    className="font-semibold line-clamp-2 mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    {blog.title}
                  </h3>
                  <p
                    className="text-sm line-clamp-2 mb-3"
                    style={{ color: 'var(--muted)' }}
                  >
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditBlog(blog)}
                        className="px-3 py-1 rounded text-sm border hover:bg-black/5 dark:hover:bg-white/5"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setCurrentBlog(blog);
                          setShowDeleteModal(true);
                        }}
                        className="px-3 py-1 rounded text-sm border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs flex items-center gap-1"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiUsers className="w-3 h-3" />
                        {blog.readingCount || 0}
                      </span>
                      <span
                        className="text-xs flex items-center gap-1"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiTrendingUp className="w-3 h-3" />
                        {blog.views || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            >
              <FiChevronLeft className="w-4 h-4" /> Previous
            </button>
            <div className="flex items-center gap-2">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg transition-all duration-200 ${
                      currentPage === pageNum
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                    style={{
                      color:
                        currentPage === pageNum ? undefined : 'var(--text)',
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() =>
                setCurrentPage(prev => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            >
              Next <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      <BlogEditorModal
        isOpen={showCreateModal || showEditModal}
        mode={showCreateModal ? 'create' : 'edit'}
        initialData={showEditModal ? currentBlog : null}
        categories={categories}
        tags={tags}
        isSubmitting={isSubmitting}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
          setCurrentBlog(null);
        }}
        onSubmit={showCreateModal ? handleCreateBlog : handleUpdateBlog}
        onCategoriesUpdate={loadCategoriesAndTags}
        onTagsUpdate={() => {}}
        currentBlog={showEditModal ? currentBlog : null}
        onRefresh={() => {
          console.log('🔄 Refreshing blog list...');
          loadBlogs(pagination.currentPage);
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && currentBlog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className="relative w-full max-w-md rounded-xl p-6"
            style={{ backgroundColor: 'var(--bg)' }}
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <FiTrash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3
                className="text-lg font-semibold mb-2"
                style={{ color: 'var(--text)' }}
              >
                Delete Blog Post
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                Are you sure you want to delete "{currentBlog.title}"? This
                action cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteBlog}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comment Manager Modal */}
      {showCommentManager && selectedBlogForComments && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div
            className="relative w-full max-w-4xl my-8 rounded-xl"
            style={{ backgroundColor: 'var(--bg)' }}
          >
            <div
              className="sticky top-0 p-4 border-b flex items-center justify-between"
              style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg)',
              }}
            >
              <h2
                className="text-xl font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Manage Comments: {selectedBlogForComments.title}
              </h2>
              <button
                onClick={() => setShowCommentManager(false)}
                className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
              >
                <FiXCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 max-h-[calc(100vh-120px)] overflow-y-auto">
              <CommentManager blogId={selectedBlogForComments.id} />
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default BlogManagement;
